import { Controller } from "stimulus";
import { expBackoff, jitter } from "live_chat/backoff";

const MAX_BACKOFF_MS = 5 * 60 * 1000;
const RESUME_SPREAD_MS = 2000;
const REQUEST_TIMEOUT_MS = 10000;

// Home "Live now" / premiere banner: live counts UP from go-live; premiere counts DOWN to the scheduled
// start, flips to LIVE, then to a muted "ending soon" state during the afterparty (all in place, no reload).
export default class BannerComponent extends Controller {
  static targets = ["timer", "timerOverdue", "waitingPill", "livePill", "endingSoonPill", "ctaLabel"];
  static values = {
    timerMs: Number,
    playbackEndsAtMs: Number,
    variant: String,
    started: Boolean,
    viewersUrl: String,
    // The count comes from chat presence, which each viewer only refreshes on its 30s heartbeat.
    pollMs: { type: Number, default: 30000 },
    // performance.now() of the count on screen. A Turbo snapshot keeps it, so a stale restore polls at once.
    countedAt: Number,
  };

  connect() {
    if (!this.startedValue) {
      this.premiereTick();
      this.timerInterval = window.setInterval(() => this.premiereTick(), 1000);
    } else if (this.timerMsValue > 0) {
      this.renderTimer();
      this.timerInterval = window.setInterval(() => this.renderTimer(), 1000);
    }

    // A Turbo preview is swapped for the fresh render in a moment, and that render's banner polls.
    this.stopped = document.documentElement.hasAttribute("data-turbo-preview");
    this.failures = 0;
    // The first poll waits out what's left of the interval since the count on screen was fetched: a full
    // one for a fresh render, none for a Turbo restoration of a snapshot left longer than that.
    if (!this.countedAtValue) this.countedAtValue = performance.now();
    const delay = this.nextPollDelay();
    this.schedulePoll(Math.min(delay, Math.max(0, delay - (performance.now() - this.countedAtValue))));

    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.clearPoll();
      } else if (!this.stopped && !this.pollTimer && !this.request) {
        this.resumePoll();
      }
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  // stopped, not just cleared: a response that lands after this must not reschedule a detached poller.
  disconnect() {
    if (this.timerInterval) window.clearInterval(this.timerInterval);
    this.stopPolling();
    if (this.request) this.request.abort();
    this.request = null;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  // Moves a pending poll onto the new interval (Stimulus also calls this before connect, when none is pending).
  pollMsValueChanged() {
    if (this.pollTimer) this.schedulePoll(this.nextPollDelay());
  }

  // 27-33s by default, so tabs opened by the same push notification never poll in step.
  nextPollDelay() {
    return jitter(this.pollMsValue * 0.9, this.pollMsValue * 0.2);
  }

  // Remembers when the next poll is due even while hidden, on the monotonic clock so a wall-clock jump can't stall it.
  schedulePoll(delay) {
    if (this.stopped) return;
    this.clearPoll();
    this.nextPollAt = performance.now() + delay;
    if (!document.hidden) this.pollTimer = window.setTimeout(() => this.pollViewers(), delay);
  }

  // Resumes where the interval left off, so tab flips never burst; the jitter stays off nextPollAt so they can't defer it.
  resumePoll() {
    const remaining = Math.max(0, this.nextPollAt - performance.now());
    this.pollTimer = window.setTimeout(() => this.pollViewers(), jitter(remaining, RESUME_SPREAD_MS));
  }

  clearPoll() {
    if (this.pollTimer) window.clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  stopPolling() {
    this.stopped = true;
    this.clearPoll();
  }

  retryLater() {
    this.schedulePoll(expBackoff(this.failures, { base: this.pollMsValue, cap: MAX_BACKOFF_MS }));
    this.failures += 1;
  }

  // Phase machine off the raw client clock (cosmetic), for any not-yet-started event of either
  // kind: waiting countdown -> LIVE -> ending soon (premiere), or waiting countdown -> LIVE /
  // static overdue state (stream, webhook-driven - see the comment below).
  premiereTick() {
    const now = Date.now();

    if (this.playbackEndsAtMsValue > 0 && now >= this.playbackEndsAtMsValue) {
      this.goAfterparty();
      return;
    }

    if (now >= this.timerMsValue) {
      if (this.variantValue === "premiere") {
        // Interval deliberately kept running: goLive() doesn't start a replacement one, and a
        // later tick still needs to catch playbackEndsAtMsValue above to reach goAfterparty().
        this.goLive();
        return;
      }

      // Webhook-driven - reaching starts_at doesn't mean the creator has actually gone live.
      // Stop ticking and show a static state; unlike the live page there's no turbo_stream
      // subscription on the home page to push a later update, so this settles for real on the
      // next natural page load once mark_live! has actually landed.
      if (this.timerInterval) window.clearInterval(this.timerInterval);
      if (this.hasTimerTarget) this.timerTarget.classList.add("hidden");
      if (this.hasTimerOverdueTarget) this.timerOverdueTarget.classList.remove("hidden");
      return;
    }

    if (this.hasTimerTarget) this.timerTarget.textContent = this.format(this.timerMsValue - now);
  }

  // live: count up from go-live time. playbackEndsAtMsValue is only ever non-zero for a
  // premiere, so this guard is a no-op for a stream - it exists so a premiere that was
  // already playing at page load (started-value true, so premiereTick never runs) still
  // flips to the afterparty state instead of counting up forever.
  renderTimer() {
    if (this.playbackEndsAtMsValue > 0 && Date.now() >= this.playbackEndsAtMsValue) {
      this.goAfterparty();
      return;
    }

    if (!this.hasTimerTarget) return;

    const elapsed = Math.max(0, Date.now() - this.timerMsValue);
    const totalSeconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(totalSeconds / 60); // not capped at 60: 63 min reads "63:xx"
    const seconds = totalSeconds % 60;
    this.timerTarget.textContent = `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  // Flip from the waiting/countdown state to the pulsing LIVE state in place (premiere only). Idempotent.
  goLive() {
    if (this.hasTimerTarget) this.timerTarget.textContent = "";
    if (this.hasWaitingPillTarget) this.waitingPillTarget.classList.add("hidden");
    if (this.hasLivePillTarget) this.livePillTarget.classList.remove("hidden");
    if (this.hasCtaLabelTarget) this.ctaLabelTarget.textContent = "Watch now";
  }

  // Episode finished playing; show the muted "ending soon" pill and stop the timer (terminal). Idempotent.
  goAfterparty() {
    if (this.timerInterval) {
      window.clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.hasTimerTarget) this.timerTarget.textContent = "";
    if (this.hasWaitingPillTarget) this.waitingPillTarget.classList.add("hidden");
    if (this.hasLivePillTarget) this.livePillTarget.classList.add("hidden");
    if (this.hasEndingSoonPillTarget) this.endingSoonPillTarget.classList.remove("hidden");
    if (this.hasCtaLabelTarget) this.ctaLabelTarget.textContent = "Join the chat";
  }

  async pollViewers() {
    this.pollTimer = null;
    if (this.stopped || this.request || !this.viewersUrlValue) return;

    const request = new AbortController();
    this.request = request;
    // A half-open connection (say, a mobile network handoff) would otherwise hold this.request forever.
    const timeout = window.setTimeout(() => request.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(this.viewersUrlValue, {
        headers: { Accept: "application/json" },
        redirect: "manual",
        signal: request.signal,
      });
      await this.handleViewersResponse(response);
    } catch {
      // A timeout, network error or non-JSON body retries; a request disconnect() aborted is no longer this.request.
      if (this.request === request) this.retryLater();
    } finally {
      window.clearTimeout(timeout);
      if (this.request === request) this.request = null;
    }
  }

  async handleViewersResponse(response) {
    // 410: a fresh load would no longer render this banner, so remove it in place; disconnect() cleans up.
    if (response.status === 410) {
      this.stopPolling();
      this.element.remove();
      return;
    }

    // A lapsed-subscription redirect (never followed) won't clear up while this page stays open.
    if (response.type === "opaqueredirect") {
      this.stopPolling();
      return;
    }

    // Anything else (a 404 for a premiere this viewer can't watch, 408, 429, 5xx) keeps the banner and backs off.
    if (!response.ok) {
      this.retryLater();
      return;
    }

    const data = await response.json();
    // Set the rolling-number controller's value attr (not textContent) so it animates.
    const el = this.element.querySelector("[data-live-viewer-count]");
    if (el) el.setAttribute("data-rolling-number-count-value", data.count);
    this.countedAtValue = performance.now();
    this.failures = 0;
    this.schedulePoll(this.nextPollDelay());
  }

  // MM:SS, widening to H:MM:SS once an hour or more remains (premiere countdown).
  format(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(value) {
    return String(value).padStart(2, "0");
  }
}
